import {NavigateFunction} from "react-router-dom";
import {getUserTokenOrRedirect} from "../../shared/services/auth.service";
import { AdminOperationTreeDto, PaginatedTreeOperationsResponse, TreeOperationsQueryParams, AiEstimatedTimeDto } from "./dtos/admin-operation-tree.dto";
import { AdminAccountClosedDto, AdminAccountDto, AdminAccountRestoredDto } from "./dtos/admin-account.dto";

export async function testAnthropic(navigate: NavigateFunction): Promise<any> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/test-antrhopic`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(async response => {
      if(!response.ok) {
        throw new Error(`Failed to call test-antrhopic: ${response.statusText}`);
      }
      return await response.json();
    });
  return response;
}

export async function getTreeOperations(
  navigate: NavigateFunction, 
  params: TreeOperationsQueryParams = {}
): Promise<PaginatedTreeOperationsResponse> {
  const token = await getUserTokenOrRedirect(navigate);
  
  // Build query string
  const queryParams = new URLSearchParams();
  if (params.pageIndex !== undefined) {
    queryParams.append('page-index', params.pageIndex.toString());
  }
  if (params.pageSize !== undefined) {
    queryParams.append('page-size', params.pageSize.toString());
  }
  if (params.operationType) {
    queryParams.append('operation-type', params.operationType);
  }
  
  const queryString = queryParams.toString();
  const url = `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/tree-operations${queryString ? `?${queryString}` : ''}`;
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
  .then(async response => {
    if(!response.ok) {
      throw new Error(`Failed to fetch tree operations: ${response.statusText}`);
    }
    return await response.json() as PaginatedTreeOperationsResponse;
  });
  return response;
} 

export async function getMostSimilarOperations(
  navigate: NavigateFunction, 
  treeOperationId: number
): Promise<AdminOperationTreeDto[]> {
  const token = await getUserTokenOrRedirect(navigate);

  const url = `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/tree-operations/${treeOperationId}/similar-operations`;
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
  .then(async response => {
    if(!response.ok) {
      throw new Error(`Failed to fetch tree operations: ${response.statusText}`);
    }
    return await response.json() as AdminOperationTreeDto[];
  });
  return response;
}

export async function getAiEstimatedTime(
  navigate: NavigateFunction, 
  treeOperationId: number
): Promise<AiEstimatedTimeDto> {
  const token = await getUserTokenOrRedirect(navigate);

  const url = `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/tree-operations/${treeOperationId}/ai-estimated-time`;
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
  .then(async response => {
    if(!response.ok) {
      throw new Error(`Failed to get AI estimated time: ${response.statusText}`);
    }
    return await response.json() as AiEstimatedTimeDto;
  });
  return response;
} 

export async function extractFeaturesFromTreeOperation(navigate: NavigateFunction, operationId: number): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate);
  await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/tree-operations/${operationId}/extract-features`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(async response => {
      if(!response.ok) {
        throw new Error(`Failed to extract features from tree operation: ${response.statusText}`);
      }
      return await response.json();
    });
}

/** A refused account request; code is the server's (reauth_required, sole_owner…) when it sent one. */
export class AdminAccountError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'AdminAccountError';
    this.status = status;
    this.code = code;
  }
}

export const needsFreshSignIn = (e: unknown) =>
  (e as {name?: string; code?: string} | null)?.name === 'AdminAccountError'
  && (e as AdminAccountError).code === 'reauth_required';

// Support's account tools. A refusal (the only owner of a team, an account
// already erased) carries the server's message, which is shown as it is.
async function adminAccountRequest<T>(navigate: NavigateFunction, path: string, method: 'GET' | 'POST'): Promise<T> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/${path}`,
    { method, headers: { 'Authorization': `Bearer ${token}` } });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { message?: string; code?: string } | null;
    throw new AdminAccountError(
      response.status, body?.code ?? undefined, body?.message || `Request failed (${response.status})`);
  }
  return await response.json() as T;
}

export const findAccounts = (navigate: NavigateFunction, query: string) =>
  adminAccountRequest<AdminAccountDto[]>(navigate, `accounts?query=${encodeURIComponent(query)}`, 'GET');

export const closeAccountAsAdmin = (navigate: NavigateFunction, userId: number) =>
  adminAccountRequest<AdminAccountClosedDto>(navigate, `accounts/${userId}/close`, 'POST');

export const restoreAccount = (navigate: NavigateFunction, userId: number) =>
  adminAccountRequest<AdminAccountRestoredDto>(navigate, `accounts/${userId}/restore`, 'POST');
